import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Calendar, Pencil } from 'lucide-react';
import EditMeetingModal from '../../../pages/Admin/Meetings/components/EditMeetingModal';
import AddMeetingModal from '../../../pages/Admin/Meetings/components/AddMeetingModal';
import EnhancedMeetingsTable from './EnhancedMeetingsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedMeetings.css';
import { supabaseMeetingService, MeetingFilters } from '../../../services/supabase/meetings';
import type { Meeting } from '../../../types/meeting';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import { format, parseISO } from 'date-fns';

// Define the meeting filter type
type MeetingFilter = {
  community: string | null;
  year: string | null;
  dateFrom: string | null;
  dateTo: string | null;
};

// Define the sort field type
type MeetingSortField = 'meet_date' | 'meet_comm';

// Define the sort config type
type SortConfig = {
  field: MeetingSortField;
  direction: 'asc' | 'desc';
};

export default function EnhancedMeetings() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MeetingFilters>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'meet_date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Fetch meetings data
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['meetings-stats', filters],
    queryFn: () => supabaseMeetingService.getStats(filters)
  });

  // Fetch all meetings for the table
  const { data: meetings = [] } = useQuery({
    queryKey: ['meetings', filters],
    queryFn: () => supabaseMeetingService.getAll(filters)
  });

  // Generate years for filter
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => (currentYear - i).toString());
  }, []);

  // Apply filters and search
  const filteredMeetings = useMemo(() => {
    if (!meetings) return [];

    return meetings.filter(meeting => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          (meeting.meet_comm || '').toLowerCase().includes(searchLower) ||
          (meeting.meet_minutes || '').toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [meetings, searchQuery]);

  // Apply sorting
  const sortedMeetings = useMemo(() => {
    return [...filteredMeetings].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'meet_date':
          return direction * (new Date(a.meet_date).getTime() - new Date(b.meet_date).getTime());
        case 'meet_comm':
          return direction * ((a.meet_comm || '').localeCompare(b.meet_comm || ''));
        default:
          return 0;
      }
    });
  }, [filteredMeetings, sortConfig]);

  // Apply pagination
  const paginatedMeetings = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedMeetings.slice(startIndex, endIndex);
  }, [sortedMeetings, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: MeetingSortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<MeetingFilters>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy');
  };

  // Handle edit modal
  const handleEditClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['meetings'] });
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedMeetings.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!stats) return {
      totalMeetings: 0,
      communitiesCount: 0,
      currentMonthMeetings: 0,
      previousMonthMeetings: 0,
      monthChange: 0,
      monthChangePercent: 0
    };

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    const currentMonthMeetings = stats.meetingsPerMonth.find(
      q => q.year === currentYear && q.month === currentMonth
    )?.count || 0;

    const previousMonthMeetings = stats.meetingsPerMonth.find(
      q => {
        if (currentMonth === 1) {
          return q.year === currentYear - 1 && q.month === 12;
        }
        return q.year === currentYear && q.month === currentMonth - 1;
      }
    )?.count || 0;

    const monthChange = currentMonthMeetings - previousMonthMeetings;
    const monthChangePercent = previousMonthMeetings === 0
      ? (currentMonthMeetings > 0 ? 100 : 0)
      : Math.round((monthChange / previousMonthMeetings) * 100);

    return {
      totalMeetings: stats.totalMeetings,
      communitiesCount: stats.communitiesWithMeetings.length,
      currentMonthMeetings,
      previousMonthMeetings,
      monthChange,
      monthChangePercent
    };
  }, [stats]);

  // Define table columns
  const columns: TableColumn<Meeting>[] = [
    {
      id: 'edit',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Meeting"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      align: 'center',
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'meet_date',
      header: 'Date',
      accessor: (row) => (
        <div className="truncate">
          {formatDate(row.meet_date)}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'meet_comm',
      header: 'Community',
      accessor: (row) => (
        <div className="truncate" title={row.meet_comm || '-'}>
          {formatTextWithLinks(row.meet_comm || '-')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'meet_minutes',
      header: 'Minutes',
      accessor: (row) => (
        <div className="wrap-text" title={row.meet_minutes || '-'}>
          {row.meet_minutes ? (
            formatTextWithLinks(row.meet_minutes)
          ) : (
            <span className="text-gray-400">No minutes</span>
          )}
        </div>
      ),
      width: 'medium' as ColumnWidth
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-4 text-red-500">
        Error loading meeting data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-meetings-container p-4">
      <div className="enhanced-meetings-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Community Meetings</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Meeting
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search meetings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.keys(filters).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredMeetings.length}
            </div>
          </div>

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({})}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Community filter */}
                <div className="filter-group">
                  <label htmlFor="community-filter">Community</label>
                  <select
                    id="community-filter"
                    value={filters.community || ''}
                    onChange={(e) => handleFilterChange({ community: e.target.value || undefined })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All communities</option>
                    {stats.communitiesWithMeetings.map(community => (
                      <option key={community} value={community}>{community}</option>
                    ))}
                  </select>
                </div>
                
                {/* Year filter */}
                <div className="filter-group">
                  <label htmlFor="year-filter">Year</label>
                  <select
                    id="year-filter"
                    value={filters.year?.toString() || ''}
                    onChange={(e) => handleFilterChange({ 
                      year: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All years</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                
                {/* Quarter filter - Commented out
                <div className="filter-group">
                  <label htmlFor="quarter-filter">Quarter</label>
                  <select
                    id="quarter-filter"
                    value={filters.quarter?.toString() || ''}
                    onChange={(e) => handleFilterChange({ 
                      quarter: e.target.value ? parseInt(e.target.value) : undefined,
                      month: undefined // Reset month when quarter changes
                    })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                    disabled={!filters.year || filters.month !== undefined}
                  >
                    <option value="">All quarters</option>
                    {[1, 2, 3, 4].map(quarter => (
                      <option key={quarter} value={quarter}>Q{quarter}</option>
                    ))}
                  </select>
                </div>
                */}
                
                {/* Month filter - Commented out
                <div className="filter-group">
                  <label htmlFor="month-filter">Month</label>
                  <select
                    id="month-filter"
                    value={filters.month?.toString() || ''}
                    onChange={(e) => handleFilterChange({ 
                      month: e.target.value ? parseInt(e.target.value) : undefined,
                      quarter: undefined // Reset quarter when month changes
                    })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                    disabled={!filters.year || filters.quarter !== undefined}
                  >
                    <option value="">All months</option>
                    {[
                      { value: 1, label: 'January' },
                      { value: 2, label: 'February' },
                      { value: 3, label: 'March' },
                      { value: 4, label: 'April' },
                      { value: 5, label: 'May' },
                      { value: 6, label: 'June' },
                      { value: 7, label: 'July' },
                      { value: 8, label: 'August' },
                      { value: 9, label: 'September' },
                      { value: 10, label: 'October' },
                      { value: 11, label: 'November' },
                      { value: 12, label: 'December' }
                    ].map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
                */}
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      {/* Add Meeting Modal */}
      {isAddModalOpen && (
        <AddMeetingModal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Meeting Modal */}
      {selectedMeeting && isEditModalOpen && (
        <EditMeetingModal
          isOpen={true}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          meeting={selectedMeeting}
        />
      )}

      <div className="enhanced-meetings-table-container enhanced-table-force-scroll">
        <EnhancedMeetingsTable
          columns={columns}
          data={paginatedMeetings}
          keyField="meet_date"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No meetings found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>
    </div>
  );
}
